import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { LogError, LogStatus, Metadata, UserInfo } from '@memberjunction/core';
import { AnnouncementData } from '../../../utils/interfaces';
import { UserService } from '../../../services/user.service';
import { NotificationService } from '../../../services/notification.service';
import { AnnouncementsEntity, AnnouncementTargetsEntity, CommitteesEntity } from 'mj_generatedentities';

@Component({
  selector: 'acs-announcement-modal',
  templateUrl: './acs-announcement-modal.html',
  styleUrls: ['./acs-announcement-modal.scss']
})
export class AcsAnnouncementModalComponent implements OnInit, OnChanges {

  @Input() isOpen = false;
  @Input() selectedChapters: CommitteesEntity[] = [];
  @Input() editMode = false;
  @Input() announcementToEdit: AnnouncementsEntity | null = null;
  @Output() modalClosed = new EventEmitter<void>();
  @Output() announcementPublished = new EventEmitter<AnnouncementData>();
  @Output() announcementUpdated = new EventEmitter<AnnouncementData>();

  currentStep = 1;
  totalSteps = 3;
  isPublishing = false;
  currentUser!: UserInfo;

  announcementData: AnnouncementData = {
    title: '',
    content: '',
    priority: 'normal',
    targetChapters: [],
    publishDate: '',
    expiryDate: '',
    notificationMethods: {
      email: false,
      portal: true,
      sms: false
    },
    requiresAcknowledgment: false,
    visibilityLevel: 'all_members'
  };

  priorityOptions = [
    { value: 'low', label: 'Low Priority', icon: '🔵', description: 'General information' },
    { value: 'normal', label: 'Normal Priority', icon: '🟢', description: 'Standard announcement' },
    { value: 'high', label: 'High Priority', icon: '🟡', description: 'Important notice' },
    { value: 'urgent', label: 'Urgent', icon: '🔴', description: 'Immediate action required' }
  ];

  visibilityOptions = [
    { value: 'all_members', label: 'All Members', description: 'Visible to all chapter members' },
    { value: 'officers_only', label: 'Officers Only', description: 'Visible to chapter officers only' },
    { value: 'specific_roles', label: 'Specific Roles', description: 'Custom role-based visibility' }
  ];

  constructor(
    private userService: UserService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userService.getUserInfo();
    if (this.editMode && this.announcementToEdit) {
      this.loadAnnouncementForEdit();
    } else {
      this.initializeAnnouncement();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reinitialize when modal opens or selected chapters change
    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue === true) {
        // Modal just opened - always reinitialize with current data
        this.currentStep = 1;
        this.isPublishing = false;

        if (this.editMode && this.announcementToEdit) {
          this.loadAnnouncementForEdit();
        } else {
          this.initializeAnnouncement();
        }
      }
    } else if (changes['selectedChapters'] && this.isOpen && !this.editMode) {
      // Selected chapters changed while modal is open (not in edit mode)
      this.initializeAnnouncement();
    }
  }

  initializeAnnouncement(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const oneMonth = new Date();
    oneMonth.setMonth(oneMonth.getMonth() + 1);

    this.announcementData = {
      title: '',
      content: '',
      priority: 'normal',
      targetChapters: [...this.selectedChapters],
      publishDate: this.formatDateForInput(tomorrow),
      expiryDate: this.formatDateForInput(oneMonth),
      notificationMethods: {
        email: false,
        portal: true,
        sms: false
      },
      requiresAcknowledgment: false,
      visibilityLevel: 'all_members'
    };
  }

  async loadAnnouncementForEdit(): Promise<void> {
    if (!this.announcementToEdit) return;
    
    this.announcementData = {
      title: this.announcementToEdit.Title,
      content: this.announcementToEdit.Content,
      priority: this.announcementToEdit.Priority,
      targetChapters: [...this.selectedChapters], // Will be loaded separately
      publishDate: this.formatDateForInput(new Date(this.announcementToEdit.PublishDate)),
      expiryDate: this.announcementToEdit.ExpiryDate ? 
        this.formatDateForInput(new Date(this.announcementToEdit.ExpiryDate)) : '',
      notificationMethods: {
        email: false,
        portal: true,
        sms: false
      },
      requiresAcknowledgment: this.announcementToEdit.RequiresAcknowledgment,
      visibilityLevel: this.announcementToEdit.VisibilityLevel || 'all_members'
    };
    
    // Load existing targets
    await this.loadAnnouncementTargets(this.announcementToEdit.ID);
  }

  async loadAnnouncementTargets(announcementId: string): Promise<void> {
    try {
      const { RunView } = await import('@memberjunction/core');
      const rv = new RunView();
      const result = await rv.RunView({
        EntityName: 'Announcement Targets',
        ExtraFilter: `AnnouncementID = '${announcementId}'`,
        ResultType: 'entity_object'
      });
      
      if (result.Success) {
        const targets = result.Results as AnnouncementTargetsEntity[];
        // Map targets back to selected chapters
        this.announcementData.targetChapters = this.selectedChapters.filter(chapter =>
          targets.some(t => t.TargetID === chapter.ID && t.TargetType === 'Chapter')
        );
      }
    } catch (error) {
      LogError('Failed to load announcement targets:', undefined, error);
    }
  }

  formatDateForInput(date: Date): string {
    return date.toISOString().slice(0, 16);
  }

  onModalClose(): void {
    this.currentStep = 1;
    this.isPublishing = false;
    this.modalClosed.emit();
  }

  canProceedToStep(step: number): boolean {
    switch (step) {
      case 2:
        return this.announcementData.title.trim().length > 0 && 
               this.announcementData.content.trim().length > 0;
      case 3:
        return this.announcementData.targetChapters.length > 0 &&
               this.announcementData.publishDate.length > 0;
      default:
        return true;
    }
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps && this.canProceedToStep(this.currentStep + 1)) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step <= this.currentStep || this.canProceedToStep(step)) {
      this.currentStep = step;
    }
  }

  onContentInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.announcementData.content = target.value;
  }

  insertTextAtCursor(textArea: HTMLTextAreaElement, text: string): void {
    const startPos = textArea.selectionStart;
    const endPos = textArea.selectionEnd;
    const content = this.announcementData.content;
    
    this.announcementData.content = content.substring(0, startPos) + text + content.substring(endPos);
    
    // Reset cursor position
    setTimeout(() => {
      textArea.focus();
      textArea.selectionStart = startPos + text.length;
      textArea.selectionEnd = startPos + text.length;
    });
  }

  formatText(format: string): void {
    const textArea = document.getElementById('announcementContent') as HTMLTextAreaElement;
    if (!textArea) return;

    const selectedText = this.announcementData.content.substring(
      textArea.selectionStart, 
      textArea.selectionEnd
    );

    if (selectedText) {
      let formattedText = '';
      switch (format) {
        case 'bold':
          formattedText = `**${selectedText}**`;
          break;
        case 'italic':
          formattedText = `*${selectedText}*`;
          break;
        case 'underline':
          formattedText = `__${selectedText}__`;
          break;
        case 'bullet':
          formattedText = `• ${selectedText}`;
          break;
        default:
          formattedText = selectedText;
      }

      const startPos = textArea.selectionStart;
      const endPos = textArea.selectionEnd;
      const content = this.announcementData.content;
      
      this.announcementData.content = content.substring(0, startPos) + formattedText + content.substring(endPos);
    }
  }

  toggleChapterSelection(chapter: CommitteesEntity): void {
    const index = this.announcementData.targetChapters.findIndex(c => c.ID === chapter.ID);
    if (index === -1) {
      this.announcementData.targetChapters.push(chapter);
    } else {
      this.announcementData.targetChapters.splice(index, 1);
    }
  }

  isChapterSelected(chapter: CommitteesEntity): boolean {
    return this.announcementData.targetChapters.some(c => c.ID === chapter.ID);
  }

  selectAllChapters(): void {
    this.announcementData.targetChapters = [...this.selectedChapters];
  }

  clearAllChapters(): void {
    this.announcementData.targetChapters = [];
  }

  getEstimatedReach(): number {
    return this.announcementData.targetChapters.reduce((total, chapter) => {
      switch (this.announcementData.visibilityLevel) {
        case 'officers_only':
          return total + 5; // Estimate 5 officers per chapter
        case 'all_members':
          return total + chapter.activeMemberCount;
        default:
          return total + chapter.activeMemberCount;
      }
    }, 0);
  }

  async publishAnnouncement(): Promise<void> {
    if (!this.canPublish()) return;

    this.isPublishing = true;

    try {
      const md = new Metadata();
      let announcement: AnnouncementsEntity;
      
      if (this.editMode && this.announcementToEdit) {
        // Edit existing announcement
        announcement = this.announcementToEdit;
      } else {
        // Create new announcement
        announcement = await md.GetEntityObject<AnnouncementsEntity>('Announcements');
        announcement.NewRecord();
        announcement.CreatedByUserID = this.currentUser.ID;
      }
      
      // Set values using property setters
      announcement.Title = this.announcementData.title;
      announcement.Content = this.announcementData.content;
      announcement.Priority = this.announcementData.priority;
      announcement.Status = this.editMode ? announcement.Status : 'published';
      
      // Convert dates to ISO strings for GraphQL compatibility
      const publishDate = new Date(this.announcementData.publishDate);
      const expiryDate = this.announcementData.expiryDate ? new Date(this.announcementData.expiryDate) : null;
      
      announcement.PublishDate = publishDate;
      announcement.ExpiryDate = expiryDate;
      announcement.RequiresAcknowledgment = this.announcementData.requiresAcknowledgment;
      announcement.VisibilityLevel = this.announcementData.visibilityLevel;
            
      // Save announcement
      const result = await announcement.Save();
      if (result) {
        // Update targets
        if (this.editMode) {
          await this.updateAnnouncementTargets(announcement.ID);
        } else {
          await this.createAnnouncementTargets(announcement.ID);
        }

        // Emit appropriate event
        const eventData = { ...this.announcementData, id: announcement.ID };
        if (this.editMode) {
          this.announcementUpdated.emit(eventData);
        } else {
          this.announcementPublished.emit(eventData);
        }

        // Show success notification
        const targetCount = this.announcementData.targetChapters.length;
        const successMessage = this.editMode
          ? `Announcement updated successfully and sent to ${targetCount} chapter${targetCount === 1 ? '' : 's'}.`
          : `Announcement published successfully to ${targetCount} chapter${targetCount === 1 ? '' : 's'}.`;

        this.notificationService.success(
          this.editMode ? 'Announcement Updated' : 'Announcement Published',
          successMessage,
          5000
        );

        LogStatus('Announcement published successfully', undefined, announcement.ID);
        this.onModalClose();
      } else {
        throw new Error(announcement.LatestResult.Message || 'Failed to save announcement');
      }
    } catch (error) {
      LogError('Failed to publish announcement:', undefined, error);
      this.notificationService.error(
        'Publication Failed',
        `Failed to ${this.editMode ? 'update' : 'publish'} announcement. Please try again.`,
        5000
      );
    } finally {
      this.isPublishing = false;
    }
  }

  private async createAnnouncementTargets(announcementId: string): Promise<void> {
    const md = new Metadata();

    for (const chapter of this.announcementData.targetChapters) {
      try {
        const target = await md.GetEntityObject<AnnouncementTargetsEntity>('Announcement Targets');
        target.NewRecord(); // Initialize as new record
        target.AnnouncementID = announcementId;
        target.TargetType = 'Chapter';
        target.TargetID = chapter.ID;
        target.TargetName = chapter.Name || chapter.CommitteeID;
        const result = await target.Save();
        if (result) {
          LogStatus('announcement target published successfully', undefined, target.ID);
        } else {
          throw new Error(target.LatestResult.Message || 'Failed to save announcement announcement');
        }
      } catch (error) {
        LogError(`Failed to create announcement target for chapter ${chapter.Name}:`, undefined, error);
      }
    }
  }

  private async updateAnnouncementTargets(announcementId: string): Promise<void> {
    try {
      const { RunView } = await import('@memberjunction/core');
      const rv = new RunView();
      const md = new Metadata();
      
      // Get existing targets
      const result = await rv.RunView({
        EntityName: 'Announcement Targets',
        ExtraFilter: `AnnouncementID = '${announcementId}'`,
        ResultType: 'entity_object'
      });
      
      if (result.Success) {
        const existingTargets = result.Results as AnnouncementTargetsEntity[];
        
        // Delete removed targets
        for (const target of existingTargets) {
          const stillSelected = this.announcementData.targetChapters.some(
            c => c.ID === target.TargetID && target.TargetType === 'Chapter'
          );
          if (!stillSelected) {
            await target.Delete();
          }
        }

        // Add new targets
        for (const chapter of this.announcementData.targetChapters) {
          const exists = existingTargets.some(
            t => t.TargetID === chapter.ID && t.TargetType === 'Chapter'
          );
          if (!exists) {
            const target = await md.GetEntityObject<AnnouncementTargetsEntity>('Announcement Targets');
            target.NewRecord();
            target.AnnouncementID = announcementId;
            target.TargetType = 'Chapter';
            target.TargetID = chapter.ID;
            target.TargetName = chapter.Name || chapter.CommitteeID;
            await target.Save();
          }
        }
      }
    } catch (error) {
      LogError('Failed to update announcement targets:', undefined, error);
      throw error;
    }
  }

  canPublish(): boolean {
    return this.announcementData.title.trim().length > 0 &&
           this.announcementData.content.trim().length > 0 &&
           this.announcementData.targetChapters.length > 0 &&
           this.announcementData.publishDate.length > 0;
  }

  getPriorityOption(value: string) {
    return this.priorityOptions.find(option => option.value === value);
  }

  getVisibilityOption(value: string) {
    return this.visibilityOptions.find(option => option.value === value);
  }

}